import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Building2, Plus, Search } from 'lucide-react';
import Toast from '../../components/Toast';
import Nr2jOwnerCell from '../../components/nr2j/Nr2jOwnerCell.jsx';
import Nr2jOwnerFilterSelect from '../../components/nr2j/Nr2jOwnerFilterSelect.jsx';
import { useToast } from '../../hooks/useToast';
import { useNr2jOwnerFilter } from '../../hooks/useNr2jOwnerFilter.js';
import apiService from '../../services/api';
import {
  formatNr2jDate,
  nr2jStageLabel,
  nr2jStatusBadge,
  organizationDisplayName,
} from '../../utils/nr2jConstants';

export default function Nr2jClients() {
  const navigate = useNavigate();
  const { toasts, toast, removeToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [q, setQ] = useState('');
  const ownerFilter = useNr2jOwnerFilter(items);
  const { params: ownerParams } = ownerFilter;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiService.listNr2jOrganizations({
        status: 'CLIENT',
        q: q.trim() || undefined,
        ...ownerParams,
      });
      setItems(res?.organizations || []);
    } catch (error) {
      toast.error(error?.message || 'Impossible de charger les clients NR2J');
    } finally {
      setLoading(false);
    }
  }, [q, ownerParams]);

  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
  }, [load]);

  return (
    <div className="space-y-6" data-testid="nr2j-clients">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Clients NR2J</h1>
          <p className="text-sm text-gray-600">
            Sociétés converties en client NR2J (même fiche que le prospect d’origine).
          </p>
        </div>
        <Link
          to="/prospection"
          className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-800 hover:bg-gray-50"
        >
          Voir la prospection
        </Link>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative max-w-md flex-1">
          <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Rechercher une société…"
            className="w-full rounded-lg border border-gray-300 py-2 pl-9 pr-3 text-sm"
          />
        </div>
        {ownerFilter.showOwner ? (
          <Nr2jOwnerFilterSelect
            value={ownerFilter.value}
            onChange={ownerFilter.setValue}
            options={ownerFilter.options}
          />
        ) : null}
      </div>

      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        {loading ? (
          <p className="p-6 text-sm text-gray-500">Chargement…</p>
        ) : items.length === 0 ? (
          <div className="p-8 text-center">
            <Building2 className="mx-auto h-8 w-8 text-gray-300" />
            <p className="mt-3 text-sm text-gray-600">Aucun client NR2J pour le moment.</p>
            <p className="mt-1 text-xs text-gray-500">
              Convertissez un prospect depuis sa fiche société (action « Convertir en client NR2J »).
            </p>
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-100 text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
              <tr>
                <th className="px-4 py-3">Société</th>
                {ownerFilter.showOwner ? <th className="px-4 py-3">Commercial</th> : null}
                <th className="px-4 py-3">Ville</th>
                <th className="px-4 py-3">Étape</th>
                <th className="px-4 py-3">Relance</th>
                <th className="px-4 py-3">Statut</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {items.map((org) => {
                const badge = nr2jStatusBadge(org.commercialStatus);
                return (
                  <tr
                    key={org.id}
                    className="cursor-pointer hover:bg-gray-50"
                    onClick={() => navigate(`/nr2j/clients/${org.id}`)}
                  >
                    <td className="px-4 py-3 font-medium text-gray-900">
                      {organizationDisplayName(org)}
                    </td>
                    {ownerFilter.showOwner ? (
                      <td className="px-4 py-3">
                        <Nr2jOwnerCell
                          owner={org.ownerUser}
                          linkable={ownerFilter.canOpenMember}
                          viewerId={ownerFilter.viewerId}
                        />
                      </td>
                    ) : null}
                    <td className="px-4 py-3 text-gray-600">{org.ville || '—'}</td>
                    <td className="px-4 py-3 text-gray-600">{nr2jStageLabel(org.pipelineStage)}</td>
                    <td className="px-4 py-3 text-gray-600">{formatNr2jDate(org.nextFollowUpAt)}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex rounded-full border px-2 py-0.5 text-xs ${badge.className}`}>
                        {badge.label}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {toasts.map((item) => (
        <Toast key={item.id} toast={item} onClose={() => removeToast(item.id)} />
      ))}
    </div>
  );
}
